import { useState, useEffect } from 'react'
import { parseCSV } from '../utils/utils'

interface UseCsvDataProps {
  url: string
  isChat: boolean
  onError?: () => void
}

export const useCsvData = ({ url, isChat, onError }: UseCsvDataProps) => {
  const [csvData, setCsvData] = useState<string[][]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [displayedRows, setDisplayedRows] = useState(0)

  const loadMoreRows = isChat ? 10 : 50

  useEffect(() => {
    const loadCSV = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await fetch(url)
        if (!response.ok) throw new Error('Failed to load CSV')
        const text = await response.text()
        const parsed = parseCSV(text)
        if (parsed.length === 0) {
          throw new Error('CSV file is empty')
        }
        setCsvData(parsed)
        setDisplayedRows(isChat ? 10 : 50)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load CSV')
        onError?.()
      } finally {
        setIsLoading(false)
      }
    }

    loadCSV()
  }, [url, onError, isChat])

  const handleLoadMore = () => {
    setDisplayedRows(prev => Math.min(prev + loadMoreRows, csvData.length))
  }

  const displayData = csvData.slice(0, displayedRows)
  const hasMore = csvData.length > displayedRows
  const headers = csvData[0] || []
  const dataRows = displayData.slice(1)

  return {
    csvData,
    isLoading,
    error,
    displayedRows,
    loadMoreRows,
    displayData,
    hasMore,
    headers,
    dataRows,
    handleLoadMore,
  }
}
